import type { StorageHealth } from "@/types";

function formatBytes(n: number | null | undefined): string {
  if (n == null) return "—";
  const units = ["B", "KiB", "MiB", "GiB", "TiB"];
  let value = n;
  for (const unit of units) {
    if (value < 1024 || unit === units[units.length - 1]) {
      return unit === "B" ? `${Math.round(value)} ${unit}` : `${value.toFixed(1)} ${unit}`;
    }
    value /= 1024;
  }
  return `${n} B`;
}

interface StorageHealthPanelProps {
  storage: StorageHealth | null | undefined;
  onOpenDetails?: () => void;
}

export function StorageHealthPanel({ storage, onOpenDetails }: StorageHealthPanelProps) {
  if (!storage) return null;

  const bandClass =
    storage.band === "green"
      ? "ok"
      : storage.band === "yellow" || storage.band === "orange"
        ? "degraded"
        : storage.band === "unknown"
          ? "unknown"
          : "critical";

  return (
    <article
      className={`glass-panel storage-health storage-health--${bandClass}`}
      aria-label={`Storage Health ${storage.band}`}
    >
      <div className="storage-health__header">
        <div>
          <div className="storage-health__eyebrow">Storage Health</div>
          <h3 className="storage-health__title">
            {storage.band}
            {storage.filesystemUsedPercent != null
              ? ` · ${storage.filesystemUsedPercent}% used`
              : ""}
            {storage.deployBlocked ? " · DEPLOY BLOCKED" : ""}
          </h3>
          <p className="storage-health__meta">
            Free {formatBytes(storage.filesystemFreeBytes)}
            {storage.filesystemPath ? ` on ${storage.filesystemPath}` : ""}
          </p>
        </div>
        {onOpenDetails ? (
          <button type="button" className="btn" onClick={onOpenDetails}>
            Open details
          </button>
        ) : null}
      </div>

      <div className="storage-health__grid">
        <div className="storage-health__stat">
          <strong>{formatBytes(storage.dockerImagesBytes)}</strong>
          <span>Images</span>
        </div>
        <div className="storage-health__stat">
          <strong>{formatBytes(storage.dockerBuildCacheBytes)}</strong>
          <span>Build cache</span>
        </div>
        <div className="storage-health__stat">
          <strong>{formatBytes(storage.dockerVolumesBytes)}</strong>
          <span>Volumes</span>
        </div>
        <div className="storage-health__stat">
          <strong>{formatBytes(storage.dockerLogsBytes)}</strong>
          <span>Container logs</span>
        </div>
        <div className="storage-health__stat">
          <strong>{formatBytes(storage.journalBytes)}</strong>
          <span>Journal</span>
        </div>
        <div className="storage-health__stat">
          <strong>
            {storage.logRotationOk == null ? "—" : storage.logRotationOk ? "ok" : "missing"}
          </strong>
          <span>Log rotation</span>
        </div>
      </div>

      {storage.largestConsumers.length > 0 ? (
        <div className="storage-health__consumers">
          <h4 className="storage-health__subtitle">Largest consumers</h4>
          <ul>
            {storage.largestConsumers.slice(0, 6).map((c) => (
              <li key={c.name}>
                <strong>{c.name}</strong>
                <span>{formatBytes(c.bytes)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {storage.findings.length > 0 ? (
        <div className="storage-health__findings">
          <h4 className="storage-health__subtitle">Findings</h4>
          <ul>
            {storage.findings.slice(0, 4).map((f) => (
              <li key={f.id}>
                <strong>{f.id}</strong>
                <span>{f.problem}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {storage.recommendedCleanup[0] ? (
        <p className="storage-health__rec">{storage.recommendedCleanup[0]}</p>
      ) : null}

      {storage.lastCleanupReclaimedBytes != null || storage.lastCleanupOk != null ? (
        <p className="storage-health__cleanup">
          Last cleanup: ok={String(storage.lastCleanupOk)} · reclaimed{" "}
          {formatBytes(storage.lastCleanupReclaimedBytes)}
        </p>
      ) : null}
    </article>
  );
}
